import { type FC, useEffect, useState } from 'react'
import { type BaseUrl, updateUrl, createUrl, type UrlDescType } from 'services'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { CenteredCircularProgress, IconButtonWithTooltip, useGlobalMessageHandler, AlertDialog, formatDate } from 'components'
import {
  gridItemIconContainerStyles,
  textFieldStyles
} from './organization-urls-styles'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import { isEmail } from 'helpers'
import React from 'react'
import { useSessionContext } from 'session'
export interface BaseUrlCardProps {
  index: number
  originalUrlBase: BaseUrl
  title: string
  originalDisabled: boolean
  urlDescTypes: UrlDescType[]
  deleteUrl: (url: BaseUrl) => void
  triggerReload: () => void
}

export const UrlSourceCard: FC<BaseUrlCardProps> = ({
  index,
  title,
  originalUrlBase,
  urlDescTypes,
  deleteUrl,
  triggerReload,
  originalDisabled = true
}) => {
  const [url, setUrl] = useState<BaseUrl>(originalUrlBase)
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
  const [disabled, setDisabled] = useState(originalDisabled)
  const [errors, setErrors] = useState<Map<number, boolean>>()
  const [showLoading, setShowLoading] = useState(false)
  const [disabledButton, setDisabledButton] = useState(false)
  const { showSuccessMessage, showErrorMessage } = useGlobalMessageHandler()
  const { openNewTab } = useSessionContext()

  useEffect(() => {
    setUrl(originalUrlBase)
    setDisabled(originalDisabled)
  }, [originalUrlBase, originalDisabled])

  const isUrlValid = (): boolean => url.value !== '' && url.descId > 0 && url.descId !== null

  const handleUrlTypeChange = (newValue: number): void => {
    setUrl((prevUrl) => ({ ...prevUrl, descId: newValue }))
  }

  const handleDeleteClick = (): void => {
    if (url.id <= 0) {
      deleteUrl(url)
      return
    }
    setDeleteConfirmationOpen(true)
  }

  const handleDeleteConfirmationClose = (): void => {
    setDeleteConfirmationOpen(false)
  }

  const handleDeleteConfirmed = (): void => {
    try {
      deleteUrl(url)
    } catch (error) {
      showErrorMessage(`Failed to update ${title}.`)
    } finally {
      setDeleteConfirmationOpen(false)
    }
  }

  const handleEditClick = (): void => {
    setDisabled(!disabled)
  }

  const handleSaveClick = (): void => {
    if (!isUrlValid()) {
      setErrors((prevErrors) => new Map(prevErrors).set(url.id, true))
      return
    }

    setShowLoading(true)
    setDisabledButton(true)
    setDisabled(true)
    if (url.id <= 0) {
      void createUrl(url).then(value => {
        if (value > 0) {
          showSuccessMessage(`${title} saved successfully.`)
          setDisabled(!disabled)
          url.id = value
          triggerReload()
        } else {
          showErrorMessage(`Failed to update ${title}.`)
        }
      }).catch(() => {
        showErrorMessage(`Failed to update ${title}.`)
      }).finally(() => {
        setShowLoading(false)
        setDisabledButton(false)
      })
    } else {
      void updateUrl(url).then(value => {
        if (value) {
          showSuccessMessage(`${title} saved successfully.`)
          setDisabled(!disabled)
          triggerReload()
        } else {
          showErrorMessage(`Failed to update ${title}.`)
        }
      }).catch(() => {
        showErrorMessage(`Failed to update ${title}.`)
      }).finally(() => {
        setShowLoading(false)
        setDisabledButton(false)
      })
    }
  }

  const handleUrlChange = (newValue: string): void => {
    setUrl((prevUrl) => ({ ...prevUrl, value: newValue }))
  }

  const handleOpenNewPageClick = (): void => {
    openNewTab(url.value, false)
  }

  return (
    <React.Fragment>
      <CenteredCircularProgress loading={showLoading} size={70}></CenteredCircularProgress>
      <Grid container key={`grid1-${index}`} id={`grid1-${url.id}`} spacing={1}>
      <Grid item xs={title === 'URL' ? 9 : 11} key={`grid2-${index}`} id={`grid2-${url.id}`}>
        <TextField
          id={`url-${url.id}`}
          key={`url-${index}`}
          label={url.value.length !== 0 ? `Last Updated: ${formatDate(url.lastUpdated)}` : 'Please enter the new URL' }
          variant="outlined"
          value={url.value}
          sx={textFieldStyles}
          disabled={disabled}
          onChange={(event) => { handleUrlChange(event.target.value) }}

          error={errors?.get(url.id)}
          fullWidth />
      </Grid>
      {title === 'URL' &&
        <Grid item xs={2} key={`grid3-${index}`} id={`grid3-${url.id}`}>
          <FormControl fullWidth>
            <InputLabel key={`inputLabel-${index}`} id={`inputLabel-${url.id}`}>Description Type</InputLabel>
            <Select
              key={`selectDescIdLabel-${index}`}
              id={`selectDescId-${url.id}`}
              labelId={`selectDescIdLabel-${url.id}`}
              label="Description Type"
              value={url.descId}
              disabled={disabled}
              onChange={(event) => { handleUrlTypeChange(Number(event.target.value)) }}
            >
               {urlDescTypes.map((option, index) => (
                <MenuItem key={option.id} value={option.id}>
                  {index + 1}. {option.type_desc}
                </MenuItem>
               ))}

            </Select>
          </FormControl>
        </Grid>}
      <Grid item xs={1} key={`grid4-${index}`} id={`grid4-${url.id}`}>
        <Box sx={gridItemIconContainerStyles}>
          { disabled
            ? (<IconButtonWithTooltip label={`Edit ${title}`} color="primary" onClick={() => { handleEditClick() }} icon={<EditIcon />} disabled={disabledButton}/>)
            : (<IconButtonWithTooltip label={`Save ${title}`} color="primary" onClick={() => { handleSaveClick() }} icon={<SaveIcon />} disabled={disabledButton} />)}
            <IconButtonWithTooltip label={`Delete ${title}`} color="secondary" onClick={handleDeleteClick} icon={<DeleteIcon />} disabled={disabledButton} />
            <IconButtonWithTooltip label={`Open ${title}`} color="secondary" onClick={handleOpenNewPageClick} icon={<OpenInNewIcon />} disabled={disabledButton || isEmail(url) || !isUrlValid()} />
        </Box>
      </Grid>
    </Grid>
    <AlertDialog
        title="Delete Confirmation"
        contentText={`Are you sure you want to delete this ${title}?`}
        nameButtonCancel="Cancel"
        nameButtonConfirm="Delete"
        onButtonClick={handleDeleteConfirmed}
        onClose={handleDeleteConfirmationClose}
        open={deleteConfirmationOpen} />

      </React.Fragment>
  )
}
